import React, { useState } from "react";
import { Row, Col, Image, Form, Button, ListGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Card from "../../components/Card";
import animationData from "../../assets/LottieFolder/STUDENT.json";
import { Eye, EyeOff } from "lucide-react";
import Lottie from "react-lottie";
// img
import facebook from "../../assets/images/brands/fb.svg";
import google from "../../assets/images/brands/gm.svg";
import instagram from "../../assets/images/brands/im.svg";
import linkedin from "../../assets/images/brands/li.svg";

import {login} from "../../services/authentication.js"
import "../../assets/css/auth.css";
import { CSSTransition } from "react-transition-group";
import {
  callToast,
  showErrorMessages,
} from "../../services/utilityFunctions.js";

const StudentAuth = () => {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const [error, setError] = useState({ error: false, message: "" });

  return (
    <section className="login-content">
      <Row className="m-0 align-items-center bg-white vh-100">
        <Col
          md="6"
          className="d-md-block d-none p-0 mt-n1 vh-10 overflow-hidden "
        >
          <Lottie
            options={defaultOptions}
            height={"80%"}
            width={"80%"}
            className="animated-scaleX"
          />
        </Col>

        <Col
          md="6"
          className=" d-md-block d-none p-0 mt-n1 vh-100 overflow-hidden"
        >
          <Row className="pt-5  justify-content-center me-3 ms-3">
            <Row md="2" lg="2" style={{ height: "70" }}>
              {error.error ? (
                <CSSTransition
                  in={error.error}
                  timeout={300}
                  classNames="error-animation"
                  unmountOnExit
                  onEntered={(node) => {
                    node.classList.add("auth-error");

                    setTimeout(() => {
                      node.classList.remove("auth-error");
                      void node.offsetWidth; // Trigger reflow
                      node.classList.add("auth-error");
                    }, 0);
                  }}
                >
                  <div className="auth-error alert alert-danger border-0 rounded-lg shadow position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <div className="error-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="text-danger"
                        >
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                          <line x1="12" y1="9" x2="12" y2="13"></line>
                          <line x1="12" y1="17" x2="12" y2="17"></line>
                        </svg>
                      </div>
                      <div>
                        <h6 className="mb-1 text-danger">
                          Oops! Something went wrong
                        </h6>
                        <p className="small mb-0">{error.message}</p>
                      </div>
                      <button
                        type="button"
                        className="btn-close ms-auto"
                        onClick={() => setError({ error: false, message: "" })}
                      />
                    </div>
                  </div>
                </CSSTransition>
              ) : (
                <div style={{ height: "70px" }} />
              )}
            </Row>
            <Row md="11 m-5 mt-0 p-5 pt-0">
              <Card
                className="card-transparent auth-card student-hover-card"
                style={{
                  boxShadow:
                    "0 8px 32px rgba(251, 191, 36, 0.15), 0 4px 16px rgba(245, 158, 11, 0.1)",
                  borderRadius: "16px",
                  transition:
                    "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  border: "1px solid rgba(251, 191, 36, 0.1)",
                  transform: "perspective(1000px) rotateX(0deg) rotateY(0deg)",
                  transformStyle: "preserve-3d",
                }}
              >
                <Card.Body>
                  <StudentLoginForm error={error} setError={setError} />
                </Card.Body>
              </Card>
            </Row>
          </Row>
        </Col>
      </Row>

      {/* Custom hover effects for student auth card */}
      <style jsx>{`
        .student-hover-card {
          position: relative;
          cursor: pointer;
        }

        .student-hover-card:hover {
          transform: perspective(1000px) rotateX(-2deg) rotateY(3deg)
            translateY(-8px) !important;
          box-shadow: 0 20px 60px rgba(251, 191, 36, 0.25),
            0 10px 30px rgba(245, 158, 11, 0.15),
            0 0 0 1px rgba(251, 191, 36, 0.2) !important;
        }

        .student-hover-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(251, 191, 36, 0.02) 0%,
            rgba(245, 158, 11, 0.02) 100%
          );
          border-radius: 16px;
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
          z-index: 1;
        }

        .student-hover-card:hover::before {
          opacity: 1;
        }

        .student-hover-card .card-body {
          position: relative;
          z-index: 2;
        }

        /* Ensure smooth transitions for all child elements */
        .student-hover-card * {
          transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
      `}</style>
    </section>
  );
};

const StudentLoginForm = ({ error, setError }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear errors when user starts typing
    if (error.error) {
      setError({ error: false, message: "" });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    loginFunction(formData.username.trim(), formData.password.trim());
  };

  function identifyUser(input) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input) ? "EMAIL" : "USERNAME";
  }

  const loginFunction = async (username, password) => {
    setLoading(true);

    var data = {
      identity: username,
      password: password,
      type: identifyUser(username),
    };

    try {
      let res = await login(data);
      console.log("Student login response:", res);

      if (res.status === "success") {
        setLoading(false);
        callToast("success", "Welcome!", "Login successful");

        // Navigate based on student user type or to exam dashboard
        if (res.data["USER TYPE"] === "STUDENT") {
          navigate("/student/exams", { replace: true });
        } else {
          navigate("/student/dashboard", { replace: true });
        }
      } else {
        setLoading(false);
        setError({
          error: true,
          message: res.message || "Invalid credentials. Please try again.",
        });
      }
    } catch (error) {
      setLoading(false);
      setError({
        error: true,
        message: "Something went wrong. Please try again.",
      });
      console.error("Student login error:", error);
    }
  };

  return (
    <>
      <div>
        <h2 className="mb-2 text-center">Student Login</h2>
        <p className="text-center">Access your exams and results.</p>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg="12">
              <Form.Group className="form-group">
                <Form.Label md="6" htmlFor="validationDefault01">
                  Student ID/Email
                </Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  id="validationDefault01"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col lg="12" className="">
              <Form.Group className="form-group">
                <Form.Label md="6" htmlFor="validationDefault02">
                  Password
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="validationDefault02"
                    value={formData.password}
                    onChange={handleChange}
                    className="pe-5" // Add padding to make room for the icon
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      border: "none",
                      background: "none",
                      padding: "0.375rem 0.75rem",
                      cursor: "pointer",
                    }}
                  >
                    {!showPassword ? (
                      <EyeOff size={18} className="text-muted" />
                    ) : (
                      <Eye size={18} className="text-muted" />
                    )}
                  </button>
                </div>
              </Form.Group>
            </Col>
            <Col lg="12" className="d-flex justify-content-between">
              <Form.Check className="form-check mb-3">
                <Form.Check.Input type="checkbox" id="customCheck1" />
                <Form.Check.Label htmlFor="customCheck1">
                  Remember Me
                </Form.Check.Label>
              </Form.Check>
              <Link to="/student/forgot-password">Forgot Password?</Link>
            </Col>
          </Row>
          <div className="d-flex justify-content-center">
            <Button type="submit" variant="btn btn-primary" disabled={loading}>
              {loading ? (
                <div class="spinner-border spinner-border-sm" role="status">

                </div>
              ) : null}{" "}
              Sign In
            </Button>
          </div>
          <p className="text-center my-3">or access student resources?</p>
          <div className="d-flex justify-content-center">
            <ListGroup
              as="ul"
              className="list-group-horizontal list-group-flush"
            >
              <ListGroup.Item as="li" className="border-0 pb-0">
                <Link to="/student/help">
                  <Image src={facebook} alt="help" />
                </Link>
              </ListGroup.Item>
              <ListGroup.Item as="li" className="border-0 pb-0">
                <Link to="/student/guide">
                  <Image src={google} alt="guide" />
                </Link>
              </ListGroup.Item>
              <ListGroup.Item as="li" className="border-0 pb-0">
                <Link to="/student/contact">
                  <Image src={instagram} alt="contact" />
                </Link>
              </ListGroup.Item>
              <ListGroup.Item as="li" className="border-0 pb-0">
                <Link to="/student/announcements">
                  <Image src={linkedin} alt="announcements" />
                </Link>
              </ListGroup.Item>
            </ListGroup>
          </div>
          <p className="mt-3 text-center">
            Need help accessing your account?{" "}
            <Link
              to="/student/contact"
              className="text-primary"
              style={{
                textDecoration: "underline",
                cursor: "pointer",
                transition: "color 0.2s ease",
              }}
            >
              Contact Support
            </Link>
          </p>
        </Form>
      </div>
    </>
  );
};

export default StudentAuth;
